import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

// Store
import { useAuthStore } from './stores/authStore';

// Components
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthInitializer from './components/AuthInitializer';

// Pages - Lazy loaded for better performance
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const AdminLoginPage = React.lazy(() => import('./pages/admin/AdminLoginPage'));

// Dashboard & Analytics
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const TodaysPulsePage = React.lazy(() => import('./pages/dashboard/TodaysPulsePage'));
const LeadsDashboardPage = React.lazy(() => import('./pages/dashboard/LeadsDashboardPage'));
const AttendanceDashboardPage = React.lazy(() => import('./pages/dashboard/AttendanceDashboardPage'));
const AdmissionsDashboardPage = React.lazy(() => import('./pages/dashboard/AdmissionsDashboardPage'));
const HRDashboardPage = React.lazy(() => import('./pages/dashboard/HRDashboardPage'));
const AnalyticsDashboardPage = React.lazy(() => import('./pages/dashboard/AnalyticsDashboardPage'));
const AnalyticsPage = React.lazy(() => import('./pages/analytics/AnalyticsPage'));
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage'));

// User Management
const UsersPage = React.lazy(() => import('./pages/users/UsersPage'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));

// Attendance & Leave Management
const AttendancePage = React.lazy(() => import('./pages/attendance/AttendancePage'));
const LeaveManagementPage = React.lazy(() => import('./pages/leaves/LeaveManagementPage'));

// Sales & CRM
const LeadsPage = React.lazy(() => import('./pages/leads/LeadsPage'));
const LeadDetailsPage = React.lazy(() => import('./pages/leads/LeadDetailsPage'));
const SalesPage = React.lazy(() => import('./pages/sales/SalesPage'));
const LeadConfigPage = React.lazy(() => import('./pages/leads/LeadConfigPage'));
const ChatbotConfigPage = React.lazy(() => import('./pages/chatbot/ChatbotConfigPage'));
const TasksPage = React.lazy(() => import('./pages/tasks/TasksPage'));

// PR Management
const TicketsPage = React.lazy(() => import('./pages/tickets/TicketsPage'));
const TicketDetailsPage = React.lazy(() => import('./pages/tickets/TicketDetailsPage'));

// Automation & Rule Engine
const RuleBuilderPage = React.lazy(() => import('./pages/automation/RuleBuilderPage'));
const TemplateManagerPage = React.lazy(() => import('./pages/automation/TemplateManagerPage'));
const RuleAnalyticsPage = React.lazy(() => import('./pages/automation/RuleAnalyticsPage'));

// Appointments
const AppointmentsPage = React.lazy(() => import('./pages/appointments/AppointmentsPage'));

// System User Management (SUPER_ADMIN only)
const SimpleSystemAdmin = React.lazy(() => import('./pages/system-user/SimpleSystemAdmin'));

// School Management
const SchoolManagementPage = React.lazy(() => import('./pages/school/SchoolManagementPage'));
const SchoolsPage = React.lazy(() => import('./pages/schools/SchoolsPage'));
const StudentsPage = React.lazy(() => import('./pages/students/StudentsPage'));
const ClassesPage = React.lazy(() => import('./pages/classes/ClassesPage'));

// HR & Payroll
const HRPage = React.lazy(() => import('./pages/hr/HRPage'));
const PayrollPage = React.lazy(() => import('./pages/payroll/PayrollPage'));

// Settings
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const WatiSettingsPage = React.lazy(() => import('./pages/settings/WatiSettingsPage'));
const WatiConfigPage = React.lazy(() => import('./pages/settings/WatiConfigPage'));
const HolidayManagementPage = React.lazy(() => import('./pages/admin/HolidayManagementPage'));
const UserControlPage = React.lazy(() => import('./pages/admin/UserControlPage'));

// Error Pages
const NotFoundPage = React.lazy(() => import('./pages/common/NotFoundPage'));
const UnauthorizedPage = React.lazy(() => import('./pages/common/UnauthorizedPage'));

// Parent Pages
const ParentRedirectPage = React.lazy(() => import('./pages/parent/ParentRedirectPage'));

// Create QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Dashboard Wrapper Component - Routes users to appropriate dashboard
const DashboardWrapper: React.FC = () => {
  const { user } = useAuthStore();
  
  // If user is a parent, show the mobile app redirect page
  if (user?.role === 'PARENT' || (user?.roles && user.roles.includes('PARENT'))) {
    return <ParentRedirectPage />;
  }
  
  // Route users to their primary dashboard based on role
  if (user?.role) {
    // Sales roles - redirect to Today's Pulse dashboard for daily overview
    if (['SALES_MANAGER', 'SALES_REP'].includes(user.role)) {
      return <Navigate to="/dashboard/todays-pulse" replace />;
    }
    
    // HR roles - redirect to HR dashboard
    if (user.role === 'HR_MANAGER') {
      return <Navigate to="/dashboard/hr" replace />;
    }
    
    // Principal - redirect to admissions dashboard
    if (user.role === 'PRINCIPAL') {
      return <Navigate to="/dashboard/admissions" replace />;
    }
    
    // Org Head and Tenant Head - redirect to analytics dashboard for overview
    if (['ORG_HEAD', 'TENANT_HEAD'].includes(user.role)) {
      return <Navigate to="/dashboard/analytics" replace />;
    }
    
    // Super Admin - redirect to system user management
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/system-user" replace />;
    }
  }
  
  // Fallback: show general dashboard page for other roles
  return <DashboardPage />;
};

// Protected Route Component with Role-based Access
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requiredRoles?: string[];
}> = React.memo(({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, user, hasAnyRole, isHydrated } = useAuthStore();

  // Wait for hydration to complete
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && user && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
});

// Public Route Component
const PublicRoute: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const { isAuthenticated, isLoading, isHydrated } = useAuthStore();

  // Wait for hydration to complete
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
});

// Page Transition Wrapper
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
};

// Suspense Fallback
const SuspenseFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthInitializer />
          <div className="App">
            <AnimatePresence mode="wait">
              <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <PageTransition>
                          <LoginPage />
                        </PageTransition>
                      </PublicRoute>
                    }
                  />

                  {/* Super Admin Login - Separate Route */}
                  <Route
                    path="/admin/login"
                    element={
                      <PublicRoute>
                        <PageTransition>
                          <AdminLoginPage />
                        </PageTransition>
                      </PublicRoute>
                    }
                  />

                  {/* Error Routes */}
                  <Route
                    path="/unauthorized"
                    element={
                      <PageTransition>
                        <UnauthorizedPage />
                      </PageTransition>
                    }
                  />

                  {/* System User Management Routes (SUPER_ADMIN Only) - ISOLATED */}
                  <Route
                    path="/system-user"
                    element={
                      <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
                        <SimpleSystemAdmin />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    
                    {/* Dashboard & Analytics */}
                    <Route
                      path="dashboard"
                      element={
                        <PageTransition>
                          <DashboardWrapper />
                        </PageTransition>
                      }
                    />
                    
                    {/* Role-based Dashboard Routes */}
                    <Route
                      path="dashboard/todays-pulse"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <TodaysPulsePage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="dashboard/leads"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <LeadsDashboardPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="dashboard/attendance"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'HR_MANAGER']}>
                          <PageTransition>
                            <AttendanceDashboardPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="dashboard/admissions"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER']}>
                          <PageTransition>
                            <AdmissionsDashboardPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="dashboard/hr"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER']}>
                          <PageTransition>
                            <HRDashboardPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="dashboard/analytics"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER']}>
                          <PageTransition>
                            <AnalyticsDashboardPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="analytics"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER']}>
                          <PageTransition>
                            <AnalyticsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="reports"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER']}>
                          <PageTransition>
                            <ReportsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* User Management */}
                    <Route
                      path="users"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'HR_MANAGER']}>
                          <PageTransition>
                            <UsersPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="profile"
                      element={
                        <PageTransition>
                          <ProfilePage />
                        </PageTransition>
                      }
                    />
                    
                    {/* Attendance & Leave Management */}
                    <Route
                      path="attendance"
                      element={
                        <PageTransition>
                          <AttendancePage />
                        </PageTransition>
                      }
                    />
                    
                    <Route
                      path="leaves"
                      element={
                        <PageTransition>
                          <LeaveManagementPage />
                        </PageTransition>
                      }
                    />
                    
                    {/* Sales & CRM */}
                    <Route
                      path="leads"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <LeadsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="leads/:id"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <LeadDetailsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <SalesPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/config"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <LeadConfigPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* WATI WhatsApp Configuration (replaces old chatbot config) */}
                    <Route
                      path="sales/chatbot-config"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <WatiSettingsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/rules/new"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/rules/:id"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/rules/:id/edit"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/templates"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TemplateManagerPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/templates/:id/edit"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TemplateManagerPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/analytics"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleAnalyticsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/legacy"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <ChatbotConfigPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/tasks"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <TasksPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/call-logs"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <LeadsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <SalesPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="appointments"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <AppointmentsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* PR Management - Parents use mobile app only */}
                    <Route
                      path="tickets"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'PR_MANAGER', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TicketsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="tickets/:id"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'PR_MANAGER', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TicketDetailsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* School Management */}
                    <Route
                      path="school-management"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER', 'HR_MANAGER']}>
                          <PageTransition>
                            <SchoolManagementPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Student Management Sub-pages */}
                    <Route
                      path="school-management/students"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="students" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="school-management/grades"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="grades" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="school-management/academic-progression"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="progression" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="school-management/notifications"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="notifications" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="schools"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="students"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER']}>
                          <PageTransition>
                            <StudentsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="classes"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER']}>
                          <PageTransition>
                            <ClassesPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* HR & Payroll */}
                    <Route
                      path="hr"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER']}>
                          <PageTransition>
                            <HRPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="payroll"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER', 'ACCOUNTANT']}>
                          <PageTransition>
                            <PayrollPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Settings */}
                    <Route
                      path="settings"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD']}>
                          <PageTransition>
                            <SettingsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* WATI Configuration - Simple Hook-based (Alternative URL) */}
                    <Route
                      path="settings/wati-config"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <WatiConfigPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Admin Routes */}
                    <Route
                      path="admin/holidays"
                      element={
                        <ProtectedRoute requiredRoles={['ORG_HEAD', 'TENANT_HEAD']}>
                          <PageTransition>
                            <HolidayManagementPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="admin/users"
                      element={
                        <ProtectedRoute requiredRoles={['ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <UserControlPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* 404 Route */}
                  <Route
                    path="*"
                    element={
                      <PageTransition>
                        <NotFoundPage />
                      </PageTransition>
                    }
                  />
                </Routes>
              </Suspense>
            </AnimatePresence>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#374151',
                  boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '14px',
                  maxWidth: '400px',
                },
                success: {
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #22c55e',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #ef4444',
                  },
                },
                loading: {
                  iconTheme: {
                    primary: '#6366f1',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #6366f1',
                  },
                },
              }}
            />

            {/* React Query Devtools */}
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
